import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Menu, X, ChevronDown } from 'lucide-react';
import { site, nav } from '../mock/mock';

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  const isActive = (to) => (to === '/' ? location.pathname === '/' : location.pathname.startsWith(to));

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur shadow-[0_2px_20px_rgba(0,0,0,0.06)] py-2' : 'bg-white/90 py-3'
      }`}
    >
      <div className="max-w-[1240px] mx-auto px-5 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center group shrink-0">
          <img
            src="/photos/logo.webp"
            alt="KD Veterinary Physiotherapy"
            className={`w-auto transition-all duration-300 ${scrolled ? 'h-12' : 'h-14'}`}
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-9">
          {nav.map((item) =>
            item.children ? (
              <div key={item.label} className="relative group">
                <Link
                  to={item.to}
                  className={`flex items-center gap-1 text-[0.95rem] font-medium tracking-wide link-underline ${
                    isActive(item.to) ? 'text-sage-600' : 'text-ink/80 hover:text-sage-600'
                  }`}
                >
                  {item.label}
                  <ChevronDown size={15} className="mt-0.5 transition-transform group-hover:rotate-180" />
                </Link>
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 opacity-0 invisible translate-y-2 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-300">
                  <div className="bg-white rounded-xl shadow-xl border border-cream-dark py-2 w-64">
                    {item.children.map((c) => (
                      <Link
                        key={c.to}
                        to={c.to}
                        className="block px-5 py-2.5 text-sm text-ink/80 hover:bg-cream hover:text-sage-600 transition-colors"
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <Link
                key={item.to}
                to={item.to}
                className={`text-[0.95rem] font-medium tracking-wide link-underline ${
                  isActive(item.to) ? 'text-sage-600' : 'text-ink/80 hover:text-sage-600'
                }`}
              >
                {item.label}
              </Link>
            )
          )}
        </nav>

        {/* Right side */}
        <div className="hidden lg:flex items-center gap-5">
          <a href={site.phoneHref} className="flex items-center gap-2 text-ink/80 hover:text-sage-600 transition-colors">
            <Phone size={17} className="text-sage-500" />
            <span className="font-semibold text-[0.95rem]">{site.phone}</span>
          </a>
          <Link
            to="/contact"
            className="bg-sage-500 hover:bg-sage-600 text-white px-6 py-2.5 rounded-full font-semibold text-sm tracking-wide transition-all hover:shadow-lg hover:-translate-y-0.5"
          >
            Book Now
          </Link>
        </div>

        {/* Mobile toggle */}
        <button className="lg:hidden text-ink p-2" onClick={() => setMobileOpen((o) => !o)} aria-label="Menu">
          {mobileOpen ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 bg-white ${
          mobileOpen ? 'max-h-[560px] border-t border-cream-dark' : 'max-h-0'
        }`}
      >
        <div className="px-5 py-4 flex flex-col">
          {nav.map((item) => (
            <div key={item.label}>
              {item.children ? (
                <>
                  <button
                    onClick={() => setServicesOpen((o) => !o)}
                    className="w-full flex items-center justify-between py-3 text-ink font-medium"
                  >
                    {item.label}
                    <ChevronDown size={18} className={`transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {servicesOpen &&
                    item.children.map((c) => (
                      <Link key={c.to} to={c.to} className="block pl-4 py-2.5 text-sm text-ink/70">
                        {c.label}
                      </Link>
                    ))}
                </>
              ) : (
                <Link to={item.to} className="block py-3 text-ink font-medium border-b border-cream">
                  {item.label}
                </Link>
              )}
            </div>
          ))}
          <a href={site.phoneHref} className="flex items-center gap-2 py-3 text-sage-600 font-semibold">
            <Phone size={17} /> {site.phone}
          </a>
          <Link to="/contact" className="mt-2 bg-sage-500 text-white text-center px-6 py-3 rounded-full font-semibold">
            Book Now
          </Link>
        </div>
      </div>
    </header>
  );
}
